import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarPlus, Loader2, Play, RefreshCw, Repeat, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { adminSubscriptionsApi } from '@/lib/admin-subscriptions-api';
import { BILLING_OVERVIEW_KEY } from '@/lib/billing-api';
import { catalogApi } from '@/lib/catalog-api';
import { getErrorMessage } from '@/lib/errors';
import { periodSummary } from '@/lib/subscription-display';
import type { Plan, Subscription, SubscriptionStatus } from '@/types/api';

const tone: Record<SubscriptionStatus, 'success' | 'warning' | 'danger' | 'muted'> = {
  incomplete: 'muted',
  trialing: 'success',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  canceled: 'muted',
  expired: 'muted',
  abandoned: 'muted',
};

const STATUS_FILTERS: { value: SubscriptionStatus | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past due' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'canceled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
  // Kept out of the customer's view but filterable here: "did this person try to buy and give up?"
  // is a question worth being able to ask.
  { value: 'abandoned', label: 'Abandoned' },
];

/**
 * Superadmin subscription management.
 *
 * Every action takes a reason, and it is required rather than optional in the UI even though the
 * API tolerates a blank one. Months later, "why is this customer suspended" is answerable only
 * if whoever suspended them wrote it down, and an optional field in a hurry is an empty one.
 */
export function AdminSubscriptionsPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SubscriptionStatus | ''>('');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState('30');
  const [assignTo, setAssignTo] = useState('');
  const [assignPlan, setAssignPlan] = useState('');

  const subscriptions = useQuery({
    queryKey: ['admin-subscriptions', status],
    queryFn: () => adminSubscriptionsApi.list({ status: status || undefined }),
  });

  const products = useQuery({ queryKey: ['catalog-products'], queryFn: () => catalogApi.listProducts() });
  const plans: Plan[] = (products.data ?? []).flatMap((p) => p.plans);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-subscriptions'] });
    // A subscription change moves the customer's access and entitlements, so anything derived
    // from them is now stale.
    void queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY });
    void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
  };

  // What was last recorded, so an operator can see the reason went somewhere. The field itself
  // is deliberately *not* cleared: clearing it disabled every button again the instant an action
  // succeeded, which read as the page breaking after one use. Working through several customers
  // on the same ticket is also the normal case, and retyping the same sentence each time is how
  // you end up with "." in an audit trail.
  const [recorded, setRecorded] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: (fn: () => Promise<Subscription>) => fn(),
    onSuccess: () => {
      setRecorded(reason.trim());
      invalidate();
    },
  });

  const assign = useMutation({
    mutationFn: () =>
      adminSubscriptionsApi.assign(assignTo.trim(), assignPlan, days ? Number(days) : null, reason),
    onSuccess: () => {
      setAssignTo('');
      setRecorded(reason.trim());
      invalidate();
    },
  });

  const busy = act.isPending || assign.isPending;
  const error = act.error ?? assign.error;
  const reasonGiven = reason.trim() !== '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Subscriptions</h1>
        <p className="text-sm text-muted-foreground">
          Every subscription on the platform. Changes take effect immediately and move the
          customer&rsquo;s entitlements with them.
        </p>
      </div>

      {error && <Alert variant="danger">{getErrorMessage(error)}</Alert>}

      <Card>
        <CardHeader>
          <CardTitle>Assign a subscription</CardTitle>
          <CardDescription>
            Grants a plan directly, with no gateway involved. For a manual sale, a migration, or
            a pilot. It activates at once.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-[1fr_1fr_7rem_auto]">
          <div className="space-y-1.5">
            <Label htmlFor="as-customer">Customer ID</Label>
            <Input
              id="as-customer"
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="as-plan">Plan</Label>
            <Select id="as-plan" value={assignPlan} onChange={(e) => setAssignPlan(e.target.value)}>
              <option value="">Choose a plan…</option>
              {plans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} · {plan.currency} {plan.price_formatted} · {plan.billing_interval}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="as-days">Days</Label>
            <Input
              id="as-days"
              type="number"
              min={1}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={() => assign.mutate()}
              disabled={busy || assignTo.trim() === '' || assignPlan === '' || !reasonGiven}
            >
              {assign.isPending ? <Loader2 className="animate-spin" /> : null} Assign
            </Button>
          </div>
          {/* Assign needs the shared reason too, and it is the one control above the field
              rather than below it — so it has to name what it is waiting for. */}
          {!reasonGiven && (
            <p className="text-xs text-warning sm:col-span-4">
              Also needs a reason. See the field below.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Pinned, because it governs buttons that are scrolled far below it. Sitting in a card at
          the top of the page, it went out of view and left an operator looking at a row of dead
          controls with nothing on screen explaining them. */}
      <div className="sticky top-0 z-10 -mx-1 rounded-lg border border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Label htmlFor="action-reason" className="shrink-0">
            Reason
          </Label>
          <Input
            id="action-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            aria-describedby="action-reason-hint"
            className={`min-w-56 flex-1 ${reasonGiven ? '' : 'border-warning/60'}`}
          />
        </div>
        <p
          id="action-reason-hint"
          className={`mt-2 text-xs ${reasonGiven ? 'text-muted-foreground' : 'text-warning'}`}
        >
          {reasonGiven
            ? 'Recorded against whichever subscription you act on next. It stays here between actions. Change it when you move to a different customer or ticket.'
            : 'Required. Every action on this page is disabled until you enter one. It is what answers “why is this customer suspended” six months from now.'}
        </p>
        {recorded !== null && recorded !== '' && (
          <p className="mt-1 text-xs text-success">Last recorded: &ldquo;{recorded}&rdquo;</p>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STATUS_FILTERS.map((filter) => (
          <Button
            key={filter.value}
            variant={status === filter.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {subscriptions.isSuccess
              ? `${subscriptions.data.data.length} subscription${subscriptions.data.data.length === 1 ? '' : 's'}`
              : 'Subscriptions'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {subscriptions.isPending && (
            <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </p>
          )}

          {subscriptions.isSuccess && subscriptions.data.data.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No subscriptions match this filter.
            </p>
          )}

          {(subscriptions.data?.data ?? []).map((subscription) => (
            <SubscriptionRow
              key={subscription.id}
              subscription={subscription}
              plans={plans}
              busy={busy}
              reasonGiven={reasonGiven}
              onAction={(fn) => act.mutate(fn)}
              reason={reason}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function SubscriptionRow({
  subscription,
  plans,
  busy,
  reason,
  reasonGiven,
  onAction,
}: {
  subscription: Subscription;
  plans: Plan[];
  busy: boolean;
  reason: string;
  reasonGiven: boolean;
  onAction: (fn: () => Promise<Subscription>) => void;
}) {
  const [extendDays, setExtendDays] = useState('14');
  const [planId, setPlanId] = useState('');
  const api = adminSubscriptionsApi;
  const id = subscription.id;
  // Terminal is a stored fact, not a clock-derived one: a lapsed subscription still has
  // transitions available (renew, extend), whereas a cancelled one has none.
  const terminal = subscription.status === 'canceled' || subscription.status === 'expired';
  // A free trial is extended, suspended or ended, never renewed or moved: renewing would turn it
  // into a free month on trial limits, and moving it would leave a paid plan flagged as a trial.
  // The API refuses both; these buttons are not offered for it.
  const trial = subscription.free_trial;

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/20 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          {/* Labelled because the row also has a subscription id, and an unlabelled uuid beside
              an "Assign a subscription → Customer ID" form is an invitation to paste the wrong
              one. */}
          <p className="text-xs text-muted-foreground">
            Customer <span className="font-mono">{subscription.customer_id}</span>
          </p>
          <p className="text-sm font-medium">
            {trial ? (
              <>Free trial · {periodSummary(subscription)}</>
            ) : (
              <>
                {subscription.currency} {subscription.price_formatted} ·{' '}
                {subscription.billing_interval_label} · {periodSummary(subscription)}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={tone[subscription.effective_status]}>
            {subscription.effective_status_label}
          </Badge>
          {subscription.cancel_at_period_end && <Badge variant="warning">Cancelling</Badge>}
          {subscription.grants_access ? (
            <Badge variant="success">Access</Badge>
          ) : (
            <Badge variant="muted">No access</Badge>
          )}
        </div>
      </div>

      {/* Terminal subscriptions offer nothing: every transition from here is rejected by the
          domain, so showing the buttons would only invite a 409. */}
      {terminal ? (
        <p className="text-xs text-muted-foreground">
          {trial
            ? 'Finished. A customer has one free trial; assign a subscription to give them more time.'
            : 'Finished. Assign a new subscription to restore this customer.'}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {/* Said at the controls, not only at the field. A disabled button with no explanation
              beside it is indistinguishable from a broken one. */}
          {!reasonGiven && (
            <p className="w-full text-xs text-warning">
              Enter a reason above to enable these actions.
            </p>
          )}

          {!trial && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.renew(id, reason))}
            >
              <RefreshCw /> Renew
            </Button>
          )}

          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={1}
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="w-20"
              aria-label="Days to extend"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven || extendDays === ''}
              onClick={() => onAction(() => api.extend(id, Number(extendDays), reason))}
            >
              <CalendarPlus /> Extend
            </Button>
          </div>

          {subscription.effective_status === 'suspended' ? (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.resume(id, reason))}
            >
              <Play /> Resume
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.suspend(id, reason))}
            >
              <Ban /> Suspend
            </Button>
          )}

          {subscription.cancel_at_period_end && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.resume(id, reason))}
            >
              <Play /> Keep
            </Button>
          )}

          {!trial && (
            <div className="flex items-center gap-1">
              <Select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                aria-label="Move to plan"
                className="w-48"
              >
                <option value="">Move to plan…</option>
                {plans
                  .filter((p) => p.id !== subscription.plan_id)
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} · {plan.price_formatted}
                    </option>
                  ))}
              </Select>
              <Button
                variant="outline"
                size="sm"
                disabled={busy || !reasonGiven || planId === ''}
                onClick={() => onAction(() => api.changePlan(id, planId, reason))}
              >
                <Repeat /> Move
              </Button>
            </div>
          )}

          {/* A trial ends at the end of its term anyway, so "at period end" would change nothing. */}
          {!trial && (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              disabled={busy || !reasonGiven}
              onClick={() => onAction(() => api.cancel(id, reason, false))}
            >
              <XCircle /> Cancel at period end
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-danger"
            disabled={busy || !reasonGiven}
            onClick={() => onAction(() => api.cancel(id, reason, true))}
          >
            {trial ? 'End trial now' : 'Cancel now'}
          </Button>
        </div>
      )}
    </div>
  );
}
